import { useState, useEffect } from "react";
import MarketForm from "../components/MarketForm";
import { Plus, Search, Store, Home, Edit } from "lucide-react";
import { db, auth } from "../firebase";
import { useAuth } from "../context/AuthContext";
import {
  collection,
  getDocs,
  query,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import ProductModal from "../components/ProductModal";
import AdminMarketModal from "../components/AdminMarketModal";
import { useNavigate } from "react-router-dom";
import { notifyError, notifyLoadError } from "../utils/errorHandler";
import Swal from "sweetalert2";

export default function MarketSitView() {
  const { currentUser } = useAuth();
  const isPending = currentUser?.status === "pending";

  const [showSellForm, setShowSellForm] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [products, setProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showAdminModal, setShowAdminModal] = useState(false);

  const navigate = useNavigate();

  const categories = [
    "Tecnología",
    "Hogar",
    "Libros",
    "Ropa",
    "Accesorios",
    "Materiales",
    "Instrumentos",
  ];

  const fetchProducts = async () => {
    try {
      const q = query(collection(db, "market"));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setProducts(data);
      setFiltered(data);
    } catch (error) {
      console.error("Error cargando productos:", error);
      notifyLoadError("No se pudieron cargar los productos del marketplace.");
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    let filteredList = [...products];

    if (categoryFilter && categoryFilter !== "Todo" && categoryFilter !== "") {
      filteredList = filteredList.filter(
        (item) => item.category === categoryFilter
      );
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filteredList = filteredList.filter(
        (item) =>
          item.title.toLowerCase().includes(term) ||
          item.description.toLowerCase().includes(term)
      );
    }

    filteredList = filteredList.filter(
      (item) => item.status.toLowerCase() !== "eliminado"
    );

    setFiltered(filteredList);
  }, [searchTerm, categoryFilter, products]);

  // Función para obtener o crear chatId entre currentUser y vendor
  async function getOrCreateChatId(currentUserId, vendorId) {
    const ids = [currentUserId, vendorId].sort();
    const chatId = `${ids[0]}_${ids[1]}`;

    const chatDocRef = doc(db, "chats", chatId);
    const chatDocSnap = await getDoc(chatDocRef);

    if (!chatDocSnap.exists()) {
      await setDoc(chatDocRef, {
        users: ids,
        createdAt: serverTimestamp(),
      });
    }

    return chatId;
  }

  // Función para abrir chat con o sin mensaje automático
  async function openChatWithUser(vendorId, initialMessage = "") {
    const currentUserId = auth.currentUser?.uid;
    const swalWarn = (msg) => Swal.fire({ toast: true, position: "top-end", icon: "warning", title: msg, showConfirmButton: false, timer: 4000, timerProgressBar: true });
    if (!vendorId) { swalWarn("Vendedor no disponible."); return; }
    if (!currentUserId) { swalWarn("Debes iniciar sesión para contactar al vendedor."); return; }
    if (isPending) { swalWarn("Tu cuenta está pendiente de verificación. Podrás contactar vendedores una vez verificada."); return; }
    if (currentUserId === vendorId) {
      return; // no chatear con uno mismo
    }

    try {
      const chatId = await getOrCreateChatId(currentUserId, vendorId);

      if (initialMessage) {
        const encodedMessage = encodeURIComponent(initialMessage);
        navigate(`/chat/${chatId}?autoMessage=${encodedMessage}`);
      } else {
        navigate(`/chat/${chatId}`);
      }

      setSelectedProduct(null); // cerrar modal producto
    } catch (error) {
      console.error("Error abriendo chat:", error);
      notifyError("No se pudo abrir el chat. Intenta de nuevo.");
    }
  }

  return (
    <div className="min-h-screen py-20 bg-gray-50 dark:bg-gray-900 transition-colors">
      <div className="w-full max-w-5xl mx-auto px-4 space-y-6">
        {/* Top Action Buttons */}
        <div className="flex flex-wrap gap-4 justify-between items-center">
          <div className="flex gap-2">
            <button
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-white transition ${
                isPending
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
              onClick={() => {
                if (isPending) {
                  Swal.fire({ toast: true, position: "top-end", icon: "warning", title: "Podrás publicar una vez que tu cuenta sea verificada.", showConfirmButton: false, timer: 4000, timerProgressBar: true });
                  return;
                }
                setShowOptions((prev) => !prev);
              }}
              title={isPending ? "Cuenta pendiente de verificación" : ""}
            >
              <Plus size={18} /> Publicar
            </button>

            {showOptions && !isPending && (
              <div className="flex gap-2">
                <button
                  className="px-3 py-1 bg-white dark:bg-gray-700 border rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => setShowSellForm(true)}
                >
                  <Store size={16} className="inline mr-1" /> Vender
                </button>
                <button
                  className="px-3 py-1 bg-white dark:bg-gray-700 border rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => Swal.fire({ toast: true, position: "top-end", icon: "info", title: "Formulario de alquiler próximamente", showConfirmButton: false, timer: 3000 })}
                >
                  <Home size={16} className="inline mr-1" /> Alquilar
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Buscar productos..."
              className="p-2 rounded-md border w-64 bg-white text-black dark:bg-gray-800 dark:text-white dark:border-gray-700"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="text-gray-600 dark:text-gray-300" />
          </div>
        </div>

        {/* Filtro por categoría + botón administrar */}
        <div className="flex flex-wrap gap-3 pt-2 justify-between items-center">
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setCategoryFilter("Todo")}
              className={`px-3 py-1 rounded-full border transition text-sm ${
                categoryFilter === "Todo" || categoryFilter === ""
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white dark:bg-gray-700 dark:text-white"
              }`}
            >
              Todo
            </button>

            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={`px-3 py-1 rounded-full border transition text-sm ${
                  categoryFilter === cat
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white dark:bg-gray-700 dark:text-white"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <button
            onClick={() => setShowAdminModal(true)}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-full hover:bg-green-700"
          >
            <Edit size={18} /> Administrar publicaciones
          </button>
        </div>

        {/* Mostrar formulario */}
        {showSellForm && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
            <MarketForm onClose={() => setShowSellForm(false)} />
          </div>
        )}

        {/* Resultados */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-6">
          {filtered.length > 0 ? (
            filtered.map((item) => (
              <div
                key={item.id}
                className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 cursor-pointer"
                onClick={() => setSelectedProduct(item)}
              >
                <img
                  src={item.media?.[0]}
                  alt={item.title}
                  className="w-full h-40 object-contain mb-3 rounded"
                />
                <h3 className="font-semibold text-lg">{item.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {item.description.substring(0, 60)}...
                </p>
                <p className="font-bold text-blue-600 mt-2">RD$ {item.price}</p>
                <p
                  className={`font-bold mt-2 ${
                    item.status === "Disponible"
                      ? "text-green-600"
                      : item.status === "Vendido"
                      ? "text-red-600"
                      : item.status === "Casi agotados"
                      ? "text-orange-500"
                      : item.status === "Pendiente"
                      ? "text-gray-500"
                      : "text-black"
                  }`}
                >
                  {item.status}
                </p>
              </div>
            ))
          ) : (
            <p className="text-center text-gray-600 dark:text-gray-400 col-span-full">
              No se encontraron productos con los criterios de búsqueda.
            </p>
          )}
        </div>
      </div>

      {/* Modal del producto seleccionado */}
      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          openChatWithUser={openChatWithUser}
          currentUser={currentUser}
        />
      )}

      {/* Modal de administración de publicaciones */}
      {showAdminModal && (
        <AdminMarketModal
          onClose={() => setShowAdminModal(false)}
          allProducts={products}
          setProducts={setProducts}
        />
      )}
    </div>
  );
}
